import { Line, LineChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts'
import type { Delta } from '@/lib/dashboardWidgetFormat'
import { shortDayLabel } from '@/lib/dashboardWidgetFormat'
import { cn } from '@/lib/utils'

// ─── Small charts the widgets share ──────────────────────────────────────────
//
// Built to the data-viz rules this app follows: 2px lines, a single series
// needs no legend (the widget title names it), the de-emphasis hue for a
// trend with the current value in the accent, hover on every mark, and text
// in text colours rather than the series colour.

const ACCENT = '#2a78d6'
const MUTED = '#9ca3af'

/** A day-by-day trend under a stat: hover shows the day and its count. */
export function Sparkline({
  points,
  label,
  color = ACCENT,
}: {
  points: { day: string; count: number }[]
  /** What one point counts, for the tooltip ("completed"). */
  label: string
  color?: string
}) {
  if (points.length < 2) return null
  return (
    <div className='h-10 w-full' aria-hidden>
      <ResponsiveContainer width='100%' height='100%'>
        <LineChart data={points} margin={{ top: 4, right: 4, bottom: 2, left: 4 }}>
          <YAxis hide domain={[0, 'dataMax']} allowDecimals={false} />
          <Tooltip
            cursor={{ stroke: '#e5e7eb' }}
            contentStyle={{ fontSize: 11, borderRadius: 8, padding: '4px 8px' }}
            labelFormatter={(_, payload) => {
              const day = payload?.[0]?.payload?.day as string | undefined
              return day ? shortDayLabel(day) : ''
            }}
            formatter={value => [value as number, label]}
          />
          <Line
            type='monotone'
            dataKey='count'
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4, stroke: '#fff', strokeWidth: 2 }}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}

/** "▲ 12% vs previous 30 days": coloured by whether the move is good. */
export function DeltaBadge({ delta, period }: { delta: Delta; period: string }) {
  return (
    <span className='inline-flex items-center gap-1 text-[11px]'>
      <span
        className={cn(
          'rounded px-1.5 py-0.5 font-semibold',
          delta.good === true && 'bg-green-50 text-green-700',
          delta.good === false && 'bg-red-50 text-red-700',
          delta.good === null && 'bg-gray-100 text-gray-600',
        )}
      >
        {delta.text}
      </span>
      <span className='text-gray-400'>vs {period}</span>
    </span>
  )
}

export type StatusSegment = { label: string; value: number; color: string }

/**
 * Parts of one whole as a single bar (open / completed / cancelled), with a
 * 2px surface gap between segments and a legend beneath that carries the
 * numbers, so no segment depends on colour or on fitting a label inside.
 */
export function StatusBar({ segments }: { segments: StatusSegment[] }) {
  const total = segments.reduce((n, s) => n + s.value, 0)
  return (
    <div className='flex flex-col gap-2'>
      <div className='flex h-3 w-full gap-0.5 overflow-hidden rounded-full bg-gray-100' role='img' aria-label={segments.map(s => `${s.label} ${s.value}`).join(', ')}>
        {total > 0 &&
          segments
            .filter(s => s.value > 0)
            .map(s => (
              <div
                key={s.label}
                title={`${s.label}: ${s.value}`}
                className='h-full first:rounded-l-full last:rounded-r-full'
                style={{ width: `${(s.value / total) * 100}%`, backgroundColor: s.color }}
              />
            ))}
      </div>
      <ul className='flex flex-wrap gap-x-4 gap-y-1'>
        {segments.map(s => (
          <li key={s.label} className='flex items-center gap-1.5 text-[11px] text-gray-500'>
            <span className='h-2 w-2 shrink-0 rounded-sm' style={{ backgroundColor: s.color }} aria-hidden />
            {s.label}
            <span className='font-semibold tabular-nums text-gray-800'>{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export const STATUS_COLORS = {
  accent: ACCENT,
  /** Lighter steps of the accent hue, for stages that come before it. */
  accentLight: '#93c5fd',
  accentLighter: '#cbd5e1',
  good: '#16a34a',
  muted: MUTED,
  bad: '#dc2626',
} as const
